"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { type AnchorHTMLAttributes, type HTMLAttributes, type ReactNode } from "react";
import { googleAnalyticsEvent } from "~/utils/googleAnalyticsUtils";
import { useAppContext } from "~/contexts/AppContext";

// Maps a nav destination to its GA event name (Nara's link_* convention),
// derived from the href so every NavItem is tracked without per-call wiring.
// Mobile + desktop copies share an href but only the visible one is clicked, so
// this fires once per click.
const navActionForHref = (
  hrefPath: string,
  hrefSearchParams: URLSearchParams | null,
  hrefSegmentCount: number
): string | null => {
  const section = hrefSearchParams?.get("section");
  if (section === "owned") return "link_my_tee_box";
  if (section === "offers-received") return "link_my_offers";
  if (hrefPath.endsWith("/notify-me")) return "link_waitlist";
  if (hrefPath.endsWith("/group-booking")) return "link_group_booking";
  if (hrefPath.endsWith("/auctions")) return "link_auctions";
  if (hrefPath.endsWith("/events")) return "link_events";
  if (hrefPath.endsWith("/my-tee-box")) return "link_sell";
  if (hrefPath.endsWith("/watchlist")) return "link_watchlist";
  if (hrefPath.includes("/golf-buddies")) return "link_golf_partners";
  if (hrefPath.includes("/account-settings"))
    return "link_account_settings";
  // course root "/<courseId>" (single segment) = Find Times
  if (hrefSegmentCount === 1) return "link_find_tee_times";
  return null;
};

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: string;
  text: ReactNode;
  icon?: ReactNode;
  className?: string;
  // "Sell" and "My Tee Box" intentionally share the same href (?section=owned)
  // so navigating between them is a cached no-op instead of a fresh route
  // fetch — but that means a plain URL-match can't tell which one to
  // highlight. List every id that shares this destination (including this
  // item's own `id`) so the last-actually-clicked one (tracked in AppContext)
  // wins the highlight instead of both lighting up together.
  collisionGroupIds?: string[];
  // When the URL matches but nothing in collisionGroupIds was the most
  // recently clicked item (fresh page load, deep link, browser back/forward,
  // arriving via an unrelated navigation) — should THIS item be the fallback
  // highlight for the group? Exactly one member of a collision group should
  // set this.
  isDefaultInGroup?: boolean;
  // Highlight for the whole page this item represents, ignoring query params
  // entirely — e.g. "My Tee Box" should stay highlighted across all of that
  // page's section tabs (?section=owned/offers/etc.), even though its own
  // href hardcodes ?section=owned (needed so it shares a cache entry with
  // "Sell"). Without this, usePathname() never changes across those tabs but
  // the query-exact check still fails once you're off the owned section.
  matchPathOnly?: boolean;
};

type DivProps = HTMLAttributes<HTMLDivElement> & {
  text: ReactNode;
  icon?: ReactNode;
  className?: string;
};

type NavItemProps = LinkProps | DivProps;

export const NavItem = (props: NavItemProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { setIsNavigating, lastClickedNavId, setLastClickedNavId } = useAppContext();

  if ("href" in props && props.href) {
    const { text, icon, href, className, onClick, collisionGroupIds, isDefaultInGroup, matchPathOnly, ...rest } = props;
    const ownId = rest.id;

    // Split href into path and query string parts
    const [hrefPath = "", hrefQuery = ""] = href.split("?") as [string, string | undefined];
    const hrefSearchParams = hrefQuery ? new URLSearchParams(hrefQuery) : null;

    // Count non-empty path segments, e.g.:
    //   "/${courseId}"           → 1 segment  → exact match only (no startsWith)
    //   "/${courseId}/auctions"  → 2 segments → sub-path startsWith allowed
    const hrefSegmentCount = hrefPath.split("/").filter(Boolean).length;

    // Path matching:
    //   • Always allow exact pathname match.
    //   • Allow startsWith sub-path match ONLY when the href has 2+ segments
    //     (prevents the course root "/${courseId}" from matching every sub-page).
    const pathMatches =
      pathname === hrefPath ||
      (hrefSegmentCount > 1 &&
        !hrefSearchParams &&
        pathname.startsWith(hrefPath) &&
        pathname[hrefPath.length] === "/");

    // Query param matching:
    //   • If href HAS query params → all of them must be present & equal in the current URL.
    //   • If href has NO query params but the current URL DOES have query params on the
    //     same path → treat as NOT active (e.g. a bare-path item shouldn't light up while
    //     on that same path with ?section=offers-received). Distinguishing "Sell" from
    //     "My Tee Box" specifically is handled via collisionGroupIds below, since they now
    //     intentionally share the same href+query.
    const currentHasQueryParams = searchParams.toString() !== "";

    const queryMatches = hrefSearchParams
      ? [...hrefSearchParams.entries()].every(
        ([key, value]) => searchParams.get(key) === value
      )
      : !(pathname === hrefPath && currentHasQueryParams);

    // The real "am I already on this exact URL" check — used for the spinner
    // guard below regardless of highlighting, so a not-currently-winning
    // collision group member still doesn't trigger a spinner that nothing
    // will clear.
    const isCurrentUrl = pathMatches && queryMatches;

    // matchPathOnly widens "is this item within scope" to the whole page
    // (ignore query differences) instead of requiring the exact href match —
    // used by page-level items like "My Tee Box" that should stay
    // highlighted across that page's own section tabs.
    const isWithinScope = matchPathOnly ? pathMatches : isCurrentUrl;

    // When several NavItems share the exact same destination, the URL alone
    // can't say which one to highlight — defer to whichever was actually
    // clicked, falling back to this group's designated default when the last
    // click wasn't one of the colliding items (fresh load, deep link, back/
    // forward, or an unrelated navigation). This disambiguation only applies
    // while we're actually on the exact colliding URL (queryMatches) — once
    // matchPathOnly is carrying an item on a different section, there's
    // nothing to disambiguate.
    const clickedWithinGroup =
      !!collisionGroupIds && lastClickedNavId !== null && collisionGroupIds.includes(lastClickedNavId);
    const isActive =
      isWithinScope &&
      (!collisionGroupIds || !queryMatches
        ? true
        : clickedWithinGroup
          ? lastClickedNavId === ownId
          : !!isDefaultInGroup);

    const navAction = navActionForHref(
      hrefPath,
      hrefSearchParams,
      hrefSegmentCount
    );

    return (
      <Link
        prefetch={false}
        href={href}
        {...rest}
        onClick={(e) => {
          if (navAction) googleAnalyticsEvent({ action: navAction });
          if (!isCurrentUrl) setIsNavigating(true);
          setLastClickedNavId(ownId ?? null);
          onClick?.(e);
        }}
        data-active={isActive ? "true" : undefined}
        className={`flex flex-col md:flex-row justify-between md:justify-center items-center gap-1 ${isActive ? "font-semibold text-primary" : ""
          } ${className ?? ""}`}
      >
        {icon ? <div>{icon}</div> : null}
        <div>
          <div className="text-[0.75rem] md:text-sm text-center">{text}</div>
        </div>
      </Link>
    );
  }

  const { text, icon, className, ...rest } = props;
  return (
    <div
      {...(rest as HTMLAttributes<HTMLDivElement>)}
      className={`flex flex-col md:flex-row justify-between md:justify-center items-center gap-1 ${className ?? ""}`}
    >
      {icon ? <div>{icon}</div> : null}
      <div>
        <div className="text-[0.75rem] md:text-sm text-center">{text}</div>
      </div>
    </div>
  );
};
